import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchTransactions, deleteTransaction, fetchAccounts } from '../api';
import type { TransactionRow } from '../types';
import { formatCurrency } from '../utils/format';

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  /** Pre-selected category filter (set when opened from the Categories view). */
  filterCategory?: string | null;
}

export function TransactionList({ open, onClose, onChanged, filterCategory }: Props) {
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<string[]>([]);
  const [accountFilter, setAccountFilter] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTransactions(200, accountFilter || null);
      setTransactions(res.transactions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [accountFilter]);

  useEffect(() => {
    if (open) {
      void load();
      fetchAccounts()
        .then((r) => setAccounts(r.accounts.map((a) => a.number)))
        .catch(() => setAccounts([]));
    }
  }, [open, load]);

  // Reset search when the filter category changes.
  useEffect(() => {
    setSearch('');
  }, [filterCategory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transactions.filter((t) => {
      if (filterCategory && t.category !== filterCategory) return false;
      if (q && !(`${t.merchant} ${t.category}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [transactions, filterCategory, search]);

  // Totals for the currently visible (filtered) rows.
  const filteredTotals = useMemo(() => {
    let credit = 0;
    let debit = 0;
    for (const t of filtered) {
      if (t.type === 'credit') credit += t.amount;
      else debit += t.amount;
    }
    return { credit, debit, net: credit - debit, count: filtered.length };
  }, [filtered]);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await deleteTransaction(id);
      setConfirmDelete(null);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col rounded-t-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-100">
            📋 {filterCategory ? `Transactions · ${filterCategory}` : 'Recent Transactions'}
          </h3>
          <div className="flex items-center gap-2">
            {filterCategory && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-zinc-800 px-2 py-1 text-[10px] font-semibold text-zinc-400 hover:text-zinc-200"
              >
                Clear filter
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-300"
            >
              Close
            </button>
          </div>
        </div>

        {error && (
          <p className="mb-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
            {error}
          </p>
        )}

        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search merchant or category…"
            className="flex-1 rounded-lg border border-slate-700 bg-zinc-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-zinc-500"
          />
          {accounts.length > 0 && (
            <select
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              title="Filter by bank account"
              className="rounded-lg border border-slate-700 bg-zinc-800 px-2.5 py-2 text-sm text-slate-200 outline-none focus:border-zinc-500"
            >
              <option value="">All accounts</option>
              {accounts.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center text-sm text-zinc-500">
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-zinc-600">
            No transactions found.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: '55vh' }}>
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-zinc-900">
                 <tr className="border-b border-zinc-800 text-zinc-500">
                   <th className="pb-2 font-medium">Date</th>
                   <th className="pb-2 font-medium">Merchant</th>
                   <th className="pb-2 font-medium">Category</th>
                   <th className="pb-2 font-medium">Account</th>
                   <th className="pb-2 font-medium">Type</th>
                   <th className="pb-2 font-medium text-right">Amount</th>
                   <th className="pb-2 font-medium text-right">Action</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {filtered.map((t) => (
                  <tr key={t.id} className="group text-zinc-300">
                    <td className="py-2 text-zinc-500">{t.transaction_date}</td>
                    <td className="py-2 max-w-[180px] truncate">{t.merchant}</td>
                    <td className="py-2">
                      <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400">
                        {t.category}
                      </span>
                    </td>
                    <td className="py-2">
                      <span className="rounded-md bg-zinc-900 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                        {t.account ?? '—'}
                      </span>
                    </td>
                    <td className="py-2">
                      <span
                        className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                          t.type === 'credit'
                            ? 'bg-emerald-500/15 text-emerald-300'
                            : 'bg-rose-500/15 text-rose-300'
                        }`}
                      >
                        {t.type === 'credit' ? 'CREDIT' : 'DEBIT'}
                      </span>
                    </td>
                    <td className={`py-2 text-right font-semibold ${t.type === 'credit' ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                    </td>
                    <td className="py-2 text-right">
                      {confirmDelete === t.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => void handleDelete(t.id)}
                            disabled={deleting === t.id}
                            className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-rose-500 disabled:opacity-50"
                          >
                            {deleting === t.id ? '...' : 'Yes'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(null)}
                            className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 hover:text-slate-300"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(t.id)}
                          className="rounded border border-transparent px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600 opacity-0 transition hover:border-rose-500/50 hover:text-rose-400 group-hover:opacity-100"
                          title="Delete this transaction"
                        >
                          🗑
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-[11px]">
            <span className="text-zinc-500">
              {filteredTotals.count} shown{filterCategory ? ` · ${filterCategory}` : ''}
            </span>
            <div className="flex items-center gap-3 font-semibold">
              <span className="text-rose-400">
                Spent {formatCurrency(filteredTotals.debit)}
              </span>
              <span className="text-emerald-400">
                Received {formatCurrency(filteredTotals.credit)}
              </span>
              <span className={filteredTotals.net >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                Net {filteredTotals.net >= 0 ? '+' : '-'}{formatCurrency(Math.abs(filteredTotals.net))}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
